import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Snackbar,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { addDays, format } from 'date-fns';
import { scheduleService } from '../services/scheduleService';
import { orderService } from '../services/orderService';
import ScheduleGanttView, {
  alignGanttRangeStart,
  getGanttRange,
} from '../components/schedule/ScheduleGanttView';
import ScheduleColorLegend from '../components/schedule/ScheduleColorLegend';
import { realtimeService } from '../services/realtimeService';

const SCHEDULE_REALTIME_DEBOUNCE_MS = 300;
const SCHEDULE_RECONCILE_INTERVAL_MS = 60000;

/** FullCalendar calendar view: one pin per order on due date (not a span). */
function toCalendarDayEvents(events, fromStr, toStr) {
  const from = String(fromStr || '').split('T')[0];
  const toExclusive = String(toStr || '').split('T')[0];

  return (events || [])
    .map((event) => {
      const dueYmd = event.extendedProps?.dueDate;
      if (!dueYmd) return null;
      if (from && dueYmd < from) return null;
      if (toExclusive && dueYmd >= toExclusive) return null;

      const { end: _end, ...rest } = event;
      return {
        ...rest,
        start: dueYmd,
        allDay: true,
      };
    })
    .filter(Boolean);
}

const SCHEDULE_VIEW_MODE_KEY = 'wm_schedule_view_mode';

function readScheduleViewMode() {
  try {
    const value = localStorage.getItem(SCHEDULE_VIEW_MODE_KEY);
    if (value === 'calendar') return 'calendar';
    return 'gantt';
  } catch {
    return 'gantt';
  }
}

export default function Schedule() {
  const navigate = useNavigate();
  const calendarRef = useRef(null);
  const [viewMode, setViewMode] = useState(readScheduleViewMode);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [events, setEvents] = useState([]);
  const [overdueSchedule, setOverdueSchedule] = useState([]);
  const [allPastDueSchedule, setAllPastDueSchedule] = useState([]);
  const [ganttRangeStart, setGanttRangeStart] = useState(() => alignGanttRangeStart(new Date()));
  const [ganttRangeDays, setGanttRangeDays] = useState(30);
  const [saveError, setSaveError] = useState('');
  const ganttInteractionBusyRef = useRef(false);
  const pendingRealtimeRefreshRef = useRef(false);
  const viewModeRef = useRef(viewMode);
  const ganttRangeRef = useRef({ ganttRangeStart, ganttRangeDays });

  viewModeRef.current = viewMode;
  ganttRangeRef.current = { ganttRangeStart, ganttRangeDays };

  const openOrder = useCallback(
    (workOrderId) => {
      if (!workOrderId) return;
      navigate(`/work-orders/${workOrderId}?tab=work-hrs`);
    },
    [navigate]
  );

  const fetchEvents = useCallback(async (fromStr, toStr, options = {}) => {
    const { background = false } = options;
    if (!background) {
      setLoading(true);
    }
    setError('');
    try {
      const res = await scheduleService.getEvents(fromStr, toStr);
      setEvents(res?.data || []);
      setOverdueSchedule(res?.overdueSchedule || []);
      setAllPastDueSchedule(res?.allPastDueSchedule || []);
    } catch {
      if (!background) {
        setError('Failed to load schedule');
      }
      if (!background) {
        setEvents([]);
      }
    } finally {
      if (!background) {
        setLoading(false);
      }
    }
  }, []);

  const refreshScheduleData = useCallback(async (options = {}) => {
    const { background = true } = options;
    if (viewModeRef.current === 'gantt') {
      const { ganttRangeStart: rangeStart, ganttRangeDays: rangeDays } = ganttRangeRef.current;
      const { start, end } = getGanttRange(rangeStart, rangeDays);
      await fetchEvents(format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'), { background });
      return;
    }

    const api = calendarRef.current?.getApi?.();
    if (api) {
      api.refetchEvents();
    }
  }, [fetchEvents]);

  const handleGanttInteractionBusy = useCallback(
    (busy) => {
      ganttInteractionBusyRef.current = busy;
      if (!busy && pendingRealtimeRefreshRef.current) {
        pendingRealtimeRefreshRef.current = false;
        void refreshScheduleData({ background: true });
      }
    },
    [refreshScheduleData]
  );

  const queueScheduleRefresh = useCallback(() => {
    if (ganttInteractionBusyRef.current) {
      pendingRealtimeRefreshRef.current = true;
      return;
    }
    void refreshScheduleData({ background: true });
  }, [refreshScheduleData]);

  const loadEvents = useCallback(
    async (info, successCallback, failureCallback) => {
      try {
        const res = await scheduleService.getEvents(info.startStr, info.endStr);
        const rows = res?.data || [];
        const calendarRows = toCalendarDayEvents(rows, info.startStr, info.endStr);
        setEvents(rows);
        setOverdueSchedule(res?.overdueSchedule || []);
        setAllPastDueSchedule(res?.allPastDueSchedule || []);
        setError('');
        successCallback(calendarRows);
      } catch {
        setError('Failed to load schedule');
        failureCallback();
      }
    },
    []
  );

  useEffect(() => {
    try {
      localStorage.setItem(SCHEDULE_VIEW_MODE_KEY, viewMode);
    } catch {
      // ignore storage errors (private mode, quota)
    }
  }, [viewMode]);

  useEffect(() => {
    if (viewMode !== 'gantt') return;
    const { start, end } = getGanttRange(ganttRangeStart, ganttRangeDays);
    fetchEvents(format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'));
  }, [viewMode, ganttRangeStart, ganttRangeDays, fetchEvents]);

  useEffect(() => {
    let unsubscribeEvents = () => {};
    let debounceTimerId = null;
    let reconcileTimerId = null;
    const seenEventIds = new Set();

    const triggerRefresh = () => {
      if (debounceTimerId) {
        window.clearTimeout(debounceTimerId);
      }
      debounceTimerId = window.setTimeout(() => {
        queueScheduleRefresh();
      }, SCHEDULE_REALTIME_DEBOUNCE_MS);
    };

    realtimeService.setSubscriptions(['work_order']);
    realtimeService.connect();

    unsubscribeEvents = realtimeService.subscribe((event) => {
      if (event?.entity !== 'work_order') return;

      if (event?.eventId) {
        if (seenEventIds.has(event.eventId)) return;
        if (seenEventIds.size > 200) {
          const first = seenEventIds.values().next().value;
          seenEventIds.delete(first);
        }
        seenEventIds.add(event.eventId);
      }

      triggerRefresh();
    });

    reconcileTimerId = window.setInterval(() => {
      queueScheduleRefresh();
    }, SCHEDULE_RECONCILE_INTERVAL_MS);

    return () => {
      if (debounceTimerId) {
        window.clearTimeout(debounceTimerId);
      }
      if (reconcileTimerId) {
        window.clearInterval(reconcileTimerId);
      }
      unsubscribeEvents();
      realtimeService.setSubscriptions([]);
      realtimeService.disconnect();
    };
  }, [queueScheduleRefresh]);

  const handleScheduleChange = useCallback(
    async (workOrderId, dates) => {
      setSaveError('');
      try {
        await orderService.saveWorkHours(workOrderId, {
          schedule_start: dates.schedule_start,
          schedule_end: dates.schedule_end,
          schedule_dates_manual: true,
          calculate_schedule_from_hours: false,
        });
        const { start, end } = getGanttRange(ganttRangeStart, ganttRangeDays);
        await fetchEvents(format(start, 'yyyy-MM-dd'), format(end, 'yyyy-MM-dd'));
      } catch {
        setSaveError('Failed to update schedule dates');
        throw new Error('Failed to update schedule dates');
      }
    },
    [fetchEvents, ganttRangeDays, ganttRangeStart]
  );

  const handleEventDidMount = useCallback((info) => {
    const el = info.el;
    const desc = info.event.extendedProps?.description;
    const customer = info.event.extendedProps?.customerName;
    const usingDefaults = info.event.extendedProps?.usingDefaults;
    const missingHoursEstimate = info.event.extendedProps?.missingHoursEstimate;
    const dueDate = info.event.extendedProps?.dueDate;
    const status = info.event.extendedProps?.status;
    const parts = [info.event.title];
    if (customer) parts.push(customer);
    if (desc) parts.push(desc);
    if (dueDate) parts.push(`Due ${dueDate}`);
    if (status === 'shipped') parts.push('Shipped');
    else if (usingDefaults) parts.push('(schedule dates not saved on Work Hrs)');
    else if (missingHoursEstimate) parts.push('(no hours estimate on Work Hrs)');
    el.title = parts.filter(Boolean).join('\n');
    el.style.cursor = 'pointer';
  }, []);

  const calendarOptions = useMemo(
    () => ({
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin],
      initialView: 'dayGridMonth',
      headerToolbar: {
        left: 'prev,next today',
        center: 'title',
        right: 'dayGridMonth,timeGridWeek,timeGridDay',
      },
      height: 'auto',
      nowIndicator: true,
      displayEventEnd: false,
    }),
    []
  );

  const hasDefaultOnly = events.some((e) => e.extendedProps?.usingDefaults);

  return (
    <Box>
      <Box
        sx={{
          mb: 2,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Schedule
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {viewMode === 'calendar'
              ? 'Orders shown on due date.'
              : 'Gantt shows planned schedule dates. Drag a bar to move it; drag edges to change start or end.'}
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={viewMode}
          onChange={(_, value) => value && setViewMode(value)}
        >
          <ToggleButton value="calendar">Calendar</ToggleButton>
          <ToggleButton value="gantt">Gantt</ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {hasDefaultOnly && viewMode === 'gantt' && !error && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Some orders use order date / due date until you save planned dates on the Work Hrs tab.
        </Alert>
      )}

      <Card>
        <CardContent sx={{ position: 'relative', p: { xs: 1, md: 2 } }}>
          {loading && (
            <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
              <CircularProgress size={24} />
            </Box>
          )}

          <ScheduleColorLegend viewMode={viewMode} />

          {viewMode === 'calendar' ? (
            <FullCalendar
              ref={calendarRef}
              {...calendarOptions}
              events={loadEvents}
              eventClick={(info) =>
                openOrder(info.event.extendedProps?.workOrderId ?? info.event.id)
              }
              eventDidMount={handleEventDidMount}
            />
          ) : (
            <ScheduleGanttView
              events={events}
              overdueSchedule={overdueSchedule}
              allPastDueSchedule={allPastDueSchedule}
              rangeStart={ganttRangeStart}
              rangeDays={ganttRangeDays}
              onRangeDaysChange={setGanttRangeDays}
              onRangeNavigate={(days, direction) => {
                setGanttRangeStart((d) => alignGanttRangeStart(addDays(d, direction * days)));
              }}
              onEventClick={(event) =>
                openOrder(event.extendedProps?.workOrderId ?? event.id)
              }
              onScheduleChange={handleScheduleChange}
              onGanttInteractionBusyChange={handleGanttInteractionBusy}
            />
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={Boolean(saveError)}
        autoHideDuration={6000}
        onClose={() => setSaveError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setSaveError('')} sx={{ width: '100%' }}>
          {saveError}
        </Alert>
      </Snackbar>
    </Box>
  );
}
